// @fileoverview : 생산관리 > 작업지시

import { customFunctions } from "@/config/customFunction";
import { useWorkActions } from "@/features/workByManagement/hooks/useWorkActions";
import { useWorkState } from "@/features/workByManagement/hooks/useWorkState";
import { usePub, useSub } from "@/hooks";
import { theme } from "@/styles/theme";
import { Theme } from "@emotion/react";
import styled from "@emotion/styled";
import { Checkbox, Flex, Text, useMantineColorScheme } from "@mantine/core";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { DefectTypeItem } from "../ui/DefectTypeSet";

type PageKeyType = "equipment" | "plan";

interface WorkTableHeadProps {
  defectTypes: DefectTypeItem[] | null;
  pageKey: PageKeyType;
  formReset?: boolean;
}

export const WorkTableHead = ({ defectTypes, pageKey, formReset }: WorkTableHeadProps) => {
  const { t } = useTranslation();
  const isPlan = useMemo(() => pageKey === "plan", [pageKey]);

  const columns = useMemo(
    () =>
      [
        {
          labelComponent: (
            <Text fz="sm" fw={500} ta="center" span>
              {t("work.workSequence")}
            </Text>
          ),
          index: 1,
          condition: isPlan,
        },
        {
          labelComponent: (
            <Flex gap="md" justify="center" align="center" direction="column" wrap="wrap">
              <Text fz="sm" fw={500} ta="center" span>
                {t("work.lastWorkDate")}
              </Text>
            </Flex>
          ),
          index: 2,
          condition: isPlan,
        },
        {
          labelComponent: (
            <Flex gap="md" justify="center" align="center" direction="column" wrap="wrap">
              <Text fz="sm" fw={500} ta="center" span>
                {t("item.materialItemCode")}
              </Text>
            </Flex>
          ),
          index: 3,
          condition: isPlan,
        },
        {
          labelComponent: (
            <Text fz="sm" fw={500} ta="center" span>
              {t("work.id")}
            </Text>
          ),
          index: 4,
          condition: isPlan && customFunctions.ADD_WORK_ID_COLUMN,
        },
        {
          labelComponent: (
            <Text fz="sm" fw={500} ta="center" span>
              {t("work.outsourceCheck")}
            </Text>
          ),
          index: 5,
          condition: isPlan && customFunctions.ADD_EXTERNAL_OUTSOURCING_COLUMN,
        },
        {
          labelComponent: (
            <Flex gap="md" justify="center" align="center" direction="column" wrap="wrap">
              <Text fz="sm" fw={500} ta="center">
                {t("work.lineName")}
              </Text>
              <Text fz="sm" fw={500} ta="center">
                {t("equipment.name")}
              </Text>
            </Flex>
          ),
          index: 6,
          condition: true,
        },
        {
          labelComponent: (
            <Flex justify="center" align="center">
              <Text fz="sm" fw={500} ta="center" span>
                {t("routing.descript")}
              </Text>
            </Flex>
          ),
          index: 7,
          condition: isPlan && customFunctions.ADD_DESCRIPTION_COLUMN,
        },
        {
          labelComponent: (
            <Flex align="center" direction="column" wrap="wrap">
              <Flex direction="column" align="flex-start" gap="sm" wrap="wrap">
                <Text fz="sm" fw={500}>
                  {t("work.aiItem")} ({t("routing.code")})
                </Text>
                <Text fz="sm" fw={500}>
                  {t("item.name")} ({t("item.code")})
                </Text>
                <Text fz="sm" fw={500} ta="center">
                  {t("item.spec")}
                </Text>
                <Text fz="sm" fw={500} ta="center">
                  {t("work.progress")}
                </Text>
              </Flex>
            </Flex>
          ),
          index: 8,
          condition: true,
        },
        {
          labelComponent: (
            <Flex align="center" direction="column" wrap="wrap">
              <Flex direction="column" align="flex-start" gap="sm" wrap="wrap">
                <Flex gap="xs">
                  <Text fz="sm" fw={500} c={theme.colors?.blue?.[9]}>
                    {t("work.targetQuantity")}
                  </Text>
                  <Text>/</Text>
                  <Text fz="sm" fw={500} c={theme.colors?.orange?.[9]}>
                    {t("work.remainQuantity")}
                  </Text>
                </Flex>
                <Flex gap="xs">
                  <Text fz="sm" fw={500} c={theme.colors?.teal?.[9]} ta="center">
                    {t("work.totalQuantity")}
                  </Text>
                  <Text>/</Text>
                  <Text fz="sm" fw={500} c={theme.colors?.red?.[9]} ta="center">
                    {t("work.defectQuantity")}
                  </Text>
                </Flex>
              </Flex>
            </Flex>
          ),
          index: 9,
          condition: true,
        },
        {
          labelComponent: (
            <Text fz="sm" fw={500} ta="center">
              {t("work.completedQuantity")}
            </Text>
          ),
          index: 10,
          condition: true,
        },
      ]
        .filter((col) => col.condition)
        .map((col, index) => ({ ...col, index: index + 1 })),
    [
      isPlan,
      customFunctions.ADD_WORK_ID_COLUMN,
      customFunctions.ADD_EXTERNAL_OUTSOURCING_COLUMN,
      customFunctions.ADD_DESCRIPTION_COLUMN,
      t,
    ],
  );

  const refs = useRef<(HTMLTableHeaderCellElement | null)[]>([]);
  const [widths, setWidths] = useState<number[]>(new Array(columns.length).fill(0));

  const state = useWorkState();
  const actions = useWorkActions();
  const [allChecked, setAllChecked] = useState<boolean>(false);
  const publish = usePub();
  const { colorScheme } = useMantineColorScheme();

  useEffect(() => {
    setAllChecked(state.works.length === state.tempWorks.length);
  }, [state.works, state.tempWorks]);

  useEffect(() => {
    const newWidths = refs.current.map((ref) => (ref ? ref.offsetWidth : 0));
    setWidths(newWidths);
  }, [state.date, state.works, state.tempWorks, columns]);

  useSub("check", () => {
    setAllChecked(state.works.length === state.tempWorks.length);
  });

  useSub("unCheck", () => {
    setAllChecked(false);
  });

  useEffect(() => {
    if (formReset) {
      setAllChecked(false);
    }
  }, [formReset, allChecked]);

  const calculateLeftWidth = useCallback((index: number) => {
    return refs.current.slice(0, index).reduce((acc, ref) => acc + (ref ? ref.offsetWidth : 0), 0);
  }, []);

  return (
    <thead>
      <tr>
        <Th leftWidth={0} ref={(el) => (refs.current[0] = el)} theme={{ colorScheme }}>
          <Flex justify="center">
            <Checkbox
              checked={allChecked}
              onClick={(e) => {
                if (e.currentTarget.checked) {
                  publish("checkAll");
                  actions.setWorks((prevWorks) => state.tempWorks);
                  setAllChecked(true);
                } else {
                  publish("unCheckAll");
                  actions.setWorks((prevWorks) => []);
                  setAllChecked(false);
                }
              }}
            />
          </Flex>
        </Th>
        {columns.map((columns) => {
          return (
            <Th
              key={columns.index}
              leftWidth={calculateLeftWidth(columns.index)}
              ref={(el) => (refs.current[columns.index] = el)}
              theme={{ colorScheme }}
            >
              {columns.labelComponent}
            </Th>
          );
        })}

        {defectTypes?.map((defect) => (
          <th key={defect.value}>
            <Text fz="sm" fw={500} c="black" ta="center">
              {t(defect.label)}
            </Text>
          </th>
        ))}
      </tr>
    </thead>
  );
};

const Th = React.memo(
  styled.th<{ leftWidth?: number; theme?: Theme & { colorScheme: "dark" | "light" } }>`
    position: sticky;
    background-color: ${({ theme }) => (theme.colorScheme === "dark" ? "#1c1c1c" : "#f9f9f9")};
    color: ${({ theme }) => (theme.colorScheme === "dark" ? "#fff" : "#000")};
    border-right: 0.0625rem solid rgb(222, 226, 230);
    border-bottom: 0.0625rem solid rgb(222, 226, 230);
    left: ${(props) => props.leftWidth ?? 0}px;
  `,
  (prevProps, nextProps) => prevProps.leftWidth === nextProps.leftWidth,
);
